import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Divider,
  Autocomplete,
  IconButton,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DownloadIcon from "@mui/icons-material/Download";
import { orderService, purchaseCatalogService } from "../services";
import { getArticleCode } from "../utils/articleCode";
import { parseInsufficientStockAcceptError } from "../utils/orderAcceptErrors";
import {
  getLineSellingPriceTp,
  getLineMrp,
  parseSellingPriceTpInput,
  computeModifyAcceptPreviewTotal,
  buildModifyAcceptAdjustments,
  buildModifyAcceptNewLineItems,
  getModifyAcceptLineId,
  clampModifyAcceptPendingQty,
  parseModifyAcceptQtyInput,
} from "../utils/purchaseOrderLinePricing";
import { validatePositiveSellingPriceAndMrp } from "../utils/sellingPriceMrp";
import {
  getLineDivisionSlug,
  isLooseCommodityLine,
  validateModifyAcceptQuantity,
} from "../utils/quantityValidation";
import {
  getPurchaseCatalogCostPrice,
  getPurchaseCatalogMrpPrice,
} from "../utils/purchaseCatalogPricing";
import {
  normCode,
  parseModifyAcceptBulkText,
  mergeBulkEntriesIntoLineState,
  MODIFY_ACCEPT_BULK_SAMPLE_CSV,
  MODIFY_ACCEPT_BULK_SAMPLE_FILENAME,
} from "../utils/modifyAcceptBulkParser";
import { downloadCsvFile } from "../utils/productManagementUtils";
import { useDebouncedValue } from "../hooks/useDebouncedValue";

const formatAmount = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  return `৳${n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const clampMinOneInt = clampModifyAcceptPendingQty;

const productKeyFromLine = (li) =>
  String(
    li?.admin_product_id ||
      li?.product_id ||
      li?.adminProduct?.id ||
      li?.product?.id ||
      "",
  ).trim();

const ModifyAcceptOrderModal = ({ open, onClose, order, onSuccess }) => {
  const lineItems = useMemo(
    () =>
      order?.order_line_items || order?.line_items || order?.lineItems || [],
    [order],
  );

  const orgId = order?.org_id ?? order?.organization?.id ?? null;
  const orderId = order?.order_id ?? order?.id ?? null;

  const initialQuantities = useMemo(() => {
    const map = {};
    lineItems.forEach((li) => {
      const id = getModifyAcceptLineId(li);
      if (id) map[id] = li.quantity ?? 0;
    });
    return map;
  }, [lineItems]);

  const initialUnitPrices = useMemo(() => {
    const map = {};
    lineItems.forEach((li) => {
      const id = getModifyAcceptLineId(li);
      if (id) map[id] = getLineSellingPriceTp(li);
    });
    return map;
  }, [lineItems]);

  const [quantities, setQuantities] = useState(initialQuantities);
  const [unitPrices, setUnitPrices] = useState(initialUnitPrices);
  const [pendingAdds, setPendingAdds] = useState([]);
  const [listPage, setListPage] = useState(0);
  const [listRowsPerPage, setListRowsPerPage] = useState(10);
  const [listFilter, setListFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stockShortageItems, setStockShortageItems] = useState(null);
  const [bulkText, setBulkText] = useState("");
  const [bulkFeedback, setBulkFeedback] = useState(null);
  const fileInputRef = useRef(null);
  const quantitiesRef = useRef(initialQuantities);
  const unitPricesRef = useRef(initialUnitPrices);

  useEffect(() => {
    quantitiesRef.current = quantities;
  }, [quantities]);

  useEffect(() => {
    unitPricesRef.current = unitPrices;
  }, [unitPrices]);

  const [searchInput, setSearchInput] = useState("");
  const debouncedSearch = useDebouncedValue(searchInput, 400);
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    if (!open) return;
    setQuantities(initialQuantities);
    setUnitPrices(initialUnitPrices);
    setPendingAdds([]);
    setListPage(0);
    setListFilter("");
    setError(null);
    setStockShortageItems(null);
    setLoading(false);
    setBulkText("");
    setBulkFeedback(null);
    setSearchInput("");
    setSearchOptions([]);
    setSearchError(null);
    setSelectedProduct(null);
    // Reset only when the modal opens or the target order changes — not on every
    // parent re-render that recreates initialQuantities / initialUnitPrices maps.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: read latest initial maps when open/orderId changes
  }, [open, orderId]);

  useEffect(() => {
    if (!open || !orgId) {
      setSearchOptions([]);
      return;
    }
    const q = String(debouncedSearch || "").trim();
    if (!q) {
      setSearchOptions([]);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setSearchLoading(true);
        setSearchError(null);
        const res = await purchaseCatalogService.searchCatalog(orgId, {
          page: 1,
          limit: 20,
          q,
        });
        if (!cancelled) {
          setSearchOptions(Array.isArray(res?.data) ? res.data : []);
        }
      } catch (e) {
        console.error("Failed to search purchase catalog", e);
        if (!cancelled) {
          setSearchOptions([]);
          setSearchError(
            e.response?.data?.message || e.message || "Failed to search products",
          );
        }
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, debouncedSearch]);

  const existingProductIds = useMemo(() => {
    const ids = new Set();
    for (const li of lineItems) {
      const id = productKeyFromLine(li);
      if (id) ids.add(id);
    }
    return ids;
  }, [lineItems]);

  const existingArticleCodes = useMemo(() => {
    const codes = new Set();
    for (const li of lineItems) {
      const code = normCode(getArticleCode(li));
      if (code) codes.add(code);
    }
    return codes;
  }, [lineItems]);

  const pendingProductIds = useMemo(() => {
    const ids = new Set();
    for (const row of pendingAdds) {
      if (row.admin_product_id) ids.add(String(row.admin_product_id));
    }
    return ids;
  }, [pendingAdds]);

  const pendingArticleCodes = useMemo(() => {
    const codes = new Set();
    for (const row of pendingAdds) {
      const code = normCode(row.sku);
      if (code) codes.add(code);
    }
    return codes;
  }, [pendingAdds]);

  const stockWarnings = useMemo(
    () =>
      pendingAdds
        .filter(
          (row) =>
            (Number(row.quantity) || 0) >
            (Number(row.available_quantity) || 0),
        )
        .map((row) => ({
          admin_product_id: row.admin_product_id,
          product_name: row.product_name,
          sku: row.sku,
          quantity: row.quantity,
          available_quantity: Number(row.available_quantity) || 0,
        })),
    [pendingAdds],
  );

  const previewTotals = useMemo(
    () =>
      computeModifyAcceptPreviewTotal(
        lineItems,
        quantities,
        unitPrices,
        pendingAdds,
      ),
    [lineItems, quantities, unitPrices, pendingAdds],
  );

  const isZeroPreviewTotal = previewTotals <= 0;

  const validateQuantities = () => {
    for (const li of lineItems) {
      const id = getModifyAcceptLineId(li);
      if (!id) continue;
      const name =
        li.product_name || li.product?.product_name || getArticleCode(li);
      const parsed = validateModifyAcceptQuantity(
        quantities[id],
        getLineDivisionSlug(li),
        { allowZero: true },
      );
      if (!parsed.ok) {
        return `${name}: ${parsed.error}`;
      }
    }
    for (const row of pendingAdds) {
      const parsed = validateModifyAcceptQuantity(
        row.quantity,
        row.division_slug,
        { allowZero: false },
      );
      if (!parsed.ok) {
        return `${row.product_name || row.sku || "New product"}: ${parsed.error}`;
      }
    }
    return null;
  };

  const validateUnitPrices = () => {
    for (const li of lineItems) {
      const id = li.id || li.order_line_item_id;
      if (!id) continue;
      const raw = unitPrices[id];
      const parsed = parseSellingPriceTpInput(raw);
      if (!parsed.ok) {
        const name =
          li.product_name || li.product?.product_name || getArticleCode(li);
        return `${name}: ${parsed.error}`;
      }
      const mrp = getLineMrp(li);
      const priceError = validatePositiveSellingPriceAndMrp(parsed.value, mrp, {
        requireBoth: true,
      });
      if (priceError) {
        const name =
          li.product_name || li.product?.product_name || getArticleCode(li);
        return `${name}: ${priceError}`;
      }
    }
    for (const row of pendingAdds) {
      const parsed = parseSellingPriceTpInput(row.unit_price);
      if (!parsed.ok) {
        return `${row.product_name || row.sku || "New product"}: ${parsed.error}`;
      }
      const priceError = validatePositiveSellingPriceAndMrp(
        parsed.value,
        row.mrp,
        { requireBoth: true },
      );
      if (priceError) {
        return `${row.product_name || row.sku || "New product"}: ${priceError}`;
      }
      const qty = Math.floor(Number(row.quantity));
      if (!Number.isInteger(qty) || qty < 1) {
        return `${row.product_name || row.sku || "New product"}: Quantity must be at least 1.`;
      }
    }
    return null;
  };

  const buildAdjustments = () => {
    const qtyMap = {};
    for (const li of lineItems) {
      const id = getModifyAcceptLineId(li);
      if (!id) continue;
      const parsed = validateModifyAcceptQuantity(
        quantities[id],
        getLineDivisionSlug(li),
        { allowZero: true },
      );
      qtyMap[id] = parsed.ok ? parsed.value : 0;
    }
    return buildModifyAcceptAdjustments(lineItems, qtyMap, unitPrices);
  };

  const tryAddCatalogProduct = useCallback(
    (catalogRow) => {
      if (!catalogRow) return;
      const productId = String(
        catalogRow.admin_product_id || catalogRow.id || "",
      ).trim();
      const sku = getArticleCode(catalogRow);
      const skuNorm = normCode(sku);

      if (!productId) {
        setError("Selected product is missing an id.");
        return;
      }
      if (existingProductIds.has(productId) || pendingProductIds.has(productId)) {
        setError(
          "This product is already on the order. Adjust the existing line quantity instead of adding it again.",
        );
        return;
      }
      if (
        skuNorm &&
        (existingArticleCodes.has(skuNorm) || pendingArticleCodes.has(skuNorm))
      ) {
        setError(
          `Article code ${sku} is already on the order. Adjust the existing line quantity instead of adding it again.`,
        );
        return;
      }

      const unitPrice = getPurchaseCatalogCostPrice(catalogRow);
      const mrp = getPurchaseCatalogMrpPrice(catalogRow);
      const priceError = validatePositiveSellingPriceAndMrp(unitPrice, mrp, {
        requireBoth: true,
      });
      if (priceError) {
        setError(
          Number.isFinite(unitPrice)
            ? `${priceError} for this product.`
            : "Price config is required for this product.",
        );
        return;
      }

      const available = Math.max(
        0,
        Number(
          catalogRow.available_quantity ??
            catalogRow.computed_available_quantity ??
            0,
        ) || 0,
      );

      setPendingAdds((prev) => [
        {
          tempKey: `new-${productId}-${Date.now()}`,
          admin_product_id: productId,
          sku: sku || "",
          product_name:
            catalogRow.product_name || catalogRow.name || sku || "Product",
          quantity: 1,
          unit_price: unitPrice,
          current_unit_price: unitPrice,
          mrp: Number.isFinite(mrp) ? mrp : null,
          available_quantity: available,
          division_slug: catalogRow.division_slug ?? null,
        },
        ...prev,
      ]);
      setListPage(0);
      setError(null);
      setSelectedProduct(null);
      setSearchInput("");
    },
    [
      existingArticleCodes,
      existingProductIds,
      pendingArticleCodes,
      pendingProductIds,
    ],
  );

  const applyBulkFromText = (text) => {
    setBulkFeedback(null);
    const { entries, errors } = parseModifyAcceptBulkText(text);
    if (entries.length === 0) {
      setBulkFeedback({
        severity: errors.length ? "error" : "warning",
        message: errors.length
          ? errors.join("\n")
          : "Add one or more lines with article code and quantity and/or Selling Price, or choose a .txt / .csv file.",
      });
      return;
    }

    const merged = mergeBulkEntriesIntoLineState(
      lineItems,
      quantitiesRef.current,
      unitPricesRef.current,
      entries,
    );
    setQuantities(merged.quantities);
    setUnitPrices(merged.unitPrices);

    const appliedMessage = merged.unmatched.length
      ? `Updated matching lines. No order line for code(s): ${merged.unmatched.join(", ")} (use search above to add new products).`
      : "Updated quantities and Selling Prices for all matching lines.";
    const validationMessage = merged.validationErrors?.length
      ? merged.validationErrors.join("\n")
      : "";
    const errorMessage = errors.length ? errors.join("\n") : "";

    setBulkFeedback({
      severity:
        errors.length || merged.unmatched.length || validationMessage
          ? "warning"
          : "success",
      message: [appliedMessage, validationMessage, errorMessage]
        .filter(Boolean)
        .join("\n\n"),
    });
  };

  const handleDownloadSampleCsv = () => {
    downloadCsvFile(MODIFY_ACCEPT_BULK_SAMPLE_CSV, MODIFY_ACCEPT_BULK_SAMPLE_FILENAME);
  };

  const handleBulkFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const t = String(reader.result || "");
      setBulkText(t);
      applyBulkFromText(t);
    };
    reader.readAsText(file);
    event.target.value = "";
  };

  const handleSubmit = async () => {
    if (!order?.order_id && !order?.id) return;
    const unitErr = validateUnitPrices();
    if (unitErr) {
      setError(unitErr);
      return;
    }
    const qtyErr = validateQuantities();
    if (qtyErr) {
      setError(qtyErr);
      return;
    }
    if (isZeroPreviewTotal) {
      setError(
        "This order cannot be submitted with a total of ৳0. Ensure at least one line item has a quantity and Selling Price greater than zero.",
      );
      return;
    }
    try {
      setLoading(true);
      setError(null);
      setStockShortageItems(null);
      const adjustments = buildAdjustments();
      const newLineItems = buildModifyAcceptNewLineItems(pendingAdds);
      if (adjustments.length === 0 && newLineItems.length === 0) {
        setError(
          "No changes detected. Change quantities or Selling Prices, add a product, or set a line to 0 to remove it.",
        );
        return;
      }
      const orderId = order.order_id || order.id;
      await orderService.modifyAcceptOrder(orderId, adjustments, newLineItems);
      if (onSuccess) await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error modifying order:", err);
      const parsed = parseInsufficientStockAcceptError(err);
      setError(parsed.message);
      setStockShortageItems(parsed.items);
    } finally {
      setLoading(false);
    }
  };

  const currentOrderAmount = Number(order?.amount ?? 0);
  const currentPayable = Number(
    order?.payable_after_credit ?? order?.amount ?? 0,
  );
  const creditApplied = Number(order?.credit_applied_amount ?? 0);
  const previewPayable = Math.max(
    0,
    Math.round((previewTotals - creditApplied) * 100) / 100,
  );

  // Newest pending adds first, then existing lines newest-id first.
  const articleListRows = useMemo(() => {
    const existing = [...lineItems].sort((a, b) => {
      const idA = Number(getModifyAcceptLineId(a)) || 0;
      const idB = Number(getModifyAcceptLineId(b)) || 0;
      return idB - idA;
    });
    return [
      ...pendingAdds.map((row) => ({ kind: "pending", row })),
      ...existing.map((li) => ({ kind: "existing", li })),
    ];
  }, [lineItems, pendingAdds]);

  const filteredArticleRows = useMemo(() => {
    const q = String(listFilter || "").trim().toLowerCase();
    if (!q) return articleListRows;
    return articleListRows.filter((entry) => {
      if (entry.kind === "pending") {
        const code = String(entry.row?.sku || "").toLowerCase();
        const name = String(entry.row?.product_name || "").toLowerCase();
        return code.includes(q) || name.includes(q);
      }
      const code = String(getArticleCode(entry.li) || "").toLowerCase();
      const name = String(
        entry.li?.product_name || entry.li?.product?.product_name || "",
      ).toLowerCase();
      return code.includes(q) || name.includes(q);
    });
  }, [articleListRows, listFilter]);

  const pagedArticleRows = useMemo(() => {
    const start = listPage * listRowsPerPage;
    return filteredArticleRows.slice(start, start + listRowsPerPage);
  }, [filteredArticleRows, listPage, listRowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredArticleRows.length / listRowsPerPage) - 1,
    );
    if (listPage > maxPage) setListPage(maxPage);
  }, [filteredArticleRows.length, listPage, listRowsPerPage]);

  const optionLabel = (option) => {
    if (!option) return "";
    const name = option.product_name || option.name || "Product";
    const code = getArticleCode(option);
    return code ? `${name} (${code})` : name;
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 700 }}>Modify Order</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
          Set new quantities and Selling Price per line (you may increase or
          decrease). Use quantity 0 to remove a line. Search to add products that
          were not on the original order. Changes apply immediately and are
          recorded in the order timeline (no grocer acceptance required).
        </Typography>

        {error && (
          <Alert
            severity="error"
            onClose={() => {
              setError(null);
              setStockShortageItems(null);
            }}
            sx={{ mb: 2 }}
          >
            {error}
            {stockShortageItems?.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2, color: "#666" }}>
                {stockShortageItems.map((row, idx) => (
                  <Typography
                    component="li"
                    variant="body2"
                    key={`${row.inventory_id || row.sku}-${idx}`}
                    sx={{ color: "#666" }}
                  >
                    {row.product_name || row.sku || "Product"} ({row.sku || "—"}
                    ): available {row.available ?? 0}, requested{" "}
                    {row.requested ?? 0}
                  </Typography>
                ))}
              </Box>
            )}
          </Alert>
        )}

        <Box
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            bgcolor: "#fafafa",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Add products
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#666", display: "block", mb: 1.5 }}
          >
            Search by product name, article code, or barcode. Products already on
            the order cannot be added again — change their quantity in the table
            below.
          </Typography>
          {!orgId ? (
            <Alert severity="warning">
              This order is missing an organisation id, so catalog search is
              unavailable.
            </Alert>
          ) : (
            <Autocomplete
              options={searchOptions}
              loading={searchLoading}
              value={selectedProduct}
              inputValue={searchInput}
              onInputChange={(_e, value, reason) => {
                if (reason === "input" || reason === "clear") {
                  setSearchInput(value);
                }
              }}
              onChange={(_e, value) => {
                setSelectedProduct(value);
                if (value) tryAddCatalogProduct(value);
              }}
              getOptionLabel={optionLabel}
              isOptionEqualToValue={(a, b) =>
                String(a?.admin_product_id || a?.id || "") ===
                String(b?.admin_product_id || b?.id || "")
              }
              filterOptions={(x) => x}
              disabled={loading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Search products"
                  placeholder="Name, article code, barcode…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {searchLoading ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
          {searchError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {searchError}
            </Alert>
          )}
          {stockWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              Requested quantity exceeds available stock for{" "}
              {stockWarnings
                .map(
                  (w) =>
                    `${w.product_name || w.sku} (available ${w.available_quantity})`,
                )
                .join("; ")}
              . You can still submit (same as Create Purchase Order).
            </Alert>
          )}
        </Box>

        <Box
          sx={{
            mb: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            bgcolor: "#fafafa",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
            Bulk update existing lines
          </Typography>
          <Typography variant="body2" sx={{ color: "#444", mb: 1.5 }}>
            Paste one product per line, or upload a <code>.txt</code> /{" "}
            <code>.csv</code> file. Only lines already on this order are
            updated — use product search above to add new products.
          </Typography>
          <Box
            component="ul"
            sx={{
              m: 0,
              mb: 1.5,
              pl: 2.5,
              color: "#555",
              fontSize: "0.875rem",
              "& li": { mb: 0.75 },
            }}
          >
            <li>
              <strong>Quantity only:</strong>{" "}
              <code>2400136, 5</code>
            </li>
            <li>
              <strong>Selling Price only</strong> (not MRP):{" "}
              <code>2400137, 25.50</code> or whole numbers{" "}
              <code>2400139,, 100</code> (note the empty middle field)
            </li>
            <li>
              <strong>Both quantity and Selling Price:</strong>{" "}
              <code>2400138, 10, 30.00</code>
            </li>
            <li>
              <strong>CSV with headers:</strong> use column{" "}
              <code>selling_price_tp</code> (download sample below). MRP is
              read-only and cannot be bulk-edited.
            </li>
          </Box>
          <Typography
            variant="caption"
            sx={{ color: "#888", display: "block", mb: 1 }}
          >
            Tip: Without the empty middle field, <code>2400139, 100</code> is
            treated as quantity 100, not price 100.
          </Typography>
          <TextField
            multiline
            minRows={4}
            fullWidth
            size="small"
            placeholder={
              "2400136, 5\n2400137, 25.50\n2400138, 10, 30.00\n2400139,, 100"
            }
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            disabled={loading}
            sx={{ mb: 1, bgcolor: "#fff" }}
          />
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="small"
              onClick={() => applyBulkFromText(bulkText)}
              disabled={loading}
            >
              Apply bulk
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              Upload file…
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadSampleCsv}
              disabled={loading}
            >
              Download sample CSV
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.csv,text/plain,text/csv"
              hidden
              onChange={handleBulkFile}
            />
          </Stack>
          {bulkFeedback && (
            <Alert
              severity={bulkFeedback.severity}
              sx={{ mt: 1.5 }}
              onClose={() => setBulkFeedback(null)}
            >
              {bulkFeedback.message}
            </Alert>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <TextField
          size="small"
          fullWidth
          label="Search lines"
          placeholder="Article code or product name"
          value={listFilter}
          onChange={(e) => {
            setListFilter(e.target.value);
            setListPage(0);
          }}
          sx={{ mb: 1.5 }}
        />

        <Box
          sx={{
            border: "1px solid #eee",
            borderRadius: 2,
            overflowX: "auto",
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "#f8f9fb" }}>
                <TableCell sx={{ fontWeight: 600 }}>Article code</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Current Qty
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  New Qty
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  MRP
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Current Selling Price
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  New Selling Price
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedArticleRows.map((entry) => {
                if (entry.kind === "pending") {
                  const row = entry.row;
                  return (
                    <TableRow key={row.tempKey} sx={{ bgcolor: "#fff8e6" }}>
                      <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                        {row.sku || "—"}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ display: "block", color: "#b26a00" }}
                        >
                          Added
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        {row.product_name}
                      </TableCell>
                      <TableCell align="right">0</TableCell>
                      <TableCell align="right" sx={{ width: 100 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={row.quantity}
                          disabled={loading}
                          inputProps={{ min: 1, step: 1 }}
                          onChange={(e) => {
                            const next = clampMinOneInt(e.target.value);
                            setPendingAdds((prev) =>
                              prev.map((p) =>
                                p.tempKey === row.tempKey
                                  ? { ...p, quantity: next }
                                  : p,
                              ),
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {row.mrp != null && Number.isFinite(Number(row.mrp))
                          ? formatAmount(row.mrp)
                          : "—"}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                        {formatAmount(
                          row.current_unit_price != null
                            ? row.current_unit_price
                            : row.unit_price,
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ width: 120 }}>
                        <TextField
                          size="small"
                          type="number"
                          value={row.unit_price}
                          disabled={loading}
                          inputProps={{ min: 0.01, step: "0.01" }}
                          onChange={(e) => {
                            setPendingAdds((prev) =>
                              prev.map((p) =>
                                p.tempKey === row.tempKey
                                  ? { ...p, unit_price: e.target.value }
                                  : p,
                              ),
                            );
                          }}
                          onBlur={(e) => {
                            const parsed = parseSellingPriceTpInput(
                              e.target.value,
                            );
                            if (parsed.ok) {
                              setPendingAdds((prev) =>
                                prev.map((p) =>
                                  p.tempKey === row.tempKey
                                    ? { ...p, unit_price: parsed.value }
                                    : p,
                                ),
                              );
                            }
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          aria-label="Remove added product"
                          disabled={loading}
                          onClick={() =>
                            setPendingAdds((prev) =>
                              prev.filter((p) => p.tempKey !== row.tempKey),
                            )
                          }
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                }

                const li = entry.li;
                const id = li.id || li.order_line_item_id;
                const currentQty = Number(li.quantity ?? 0);
                const qtyValue = quantities[id];
                const currentUnit = getLineSellingPriceTp(li);
                const lineMrp = getLineMrp(li);
                const unitValue = unitPrices[id];
                const articleCode = getArticleCode(li) || "—";
                const productName =
                  li.product_name || li.product?.product_name || "—";
                const isLooseCommodity = isLooseCommodityLine(li);
                return (
                  <TableRow key={id}>
                    <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                      {articleCode}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>{productName}</TableCell>
                    <TableCell align="right">{currentQty}</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={qtyValue}
                        disabled={loading}
                        inputProps={{
                          min: 0,
                          step: isLooseCommodity ? "0.01" : "1",
                        }}
                        onChange={(e) => {
                          setQuantities((prev) => ({
                            ...prev,
                            [id]: e.target.value,
                          }));
                        }}
                        onBlur={(e) => {
                          const parsed = parseModifyAcceptQtyInput(
                            e.target.value,
                            {
                              divisionSlug: getLineDivisionSlug(li),
                              allowZero: true,
                            },
                          );
                          if (parsed.ok) {
                            setQuantities((prev) => ({
                              ...prev,
                              [id]: parsed.value,
                            }));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {lineMrp != null ? formatAmount(lineMrp) : "—"}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                      {formatAmount(currentUnit)}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>
                      <TextField
                        size="small"
                        type="number"
                        value={unitValue}
                        disabled={loading}
                        inputProps={{ min: 0.01, step: "0.01" }}
                        onChange={(e) => {
                          setUnitPrices((prev) => ({
                            ...prev,
                            [id]: e.target.value,
                          }));
                        }}
                        onBlur={(e) => {
                          const parsed = parseSellingPriceTpInput(
                            e.target.value,
                          );
                          if (parsed.ok) {
                            setUnitPrices((prev) => ({
                              ...prev,
                              [id]: parsed.value,
                            }));
                          }
                        }}
                      />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredArticleRows.length}
            page={listPage}
            onPageChange={(_e, nextPage) => setListPage(nextPage)}
            rowsPerPage={listRowsPerPage}
            onRowsPerPageChange={(e) => {
              setListRowsPerPage(parseInt(e.target.value, 10));
              setListPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </Box>

        <Box
          sx={{
            mt: 2,
            p: 2,
            border: "1px solid #eee",
            borderRadius: 2,
            bgcolor: "#f8f9fb",
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Order total preview
          </Typography>
          <Stack spacing={0.5}>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Current order amount: {formatAmount(currentOrderAmount)}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Preview subtotal: {formatAmount(previewTotals)}
            </Typography>
            {isZeroPreviewTotal && (
              <Typography variant="body2" sx={{ color: "error.main", mt: 0.5 }}>
                This order cannot be submitted with a total of ৳0. Ensure at least
                one line item has a quantity and Selling Price greater than zero.
              </Typography>
            )}
            {creditApplied > 0 && (
              <>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Credit applied: {formatAmount(creditApplied)}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666" }}>
                  Current payable: {formatAmount(currentPayable)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Preview payable: {formatAmount(previewPayable)}
                </Typography>
              </>
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || isZeroPreviewTotal}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Submitting..." : "Modify Order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModifyAcceptOrderModal;
